/**
 * Client-side batching logger for confidential-mode usage telemetry.
 *
 * Confidential mode runs entirely in the browser, so these anonymized usage
 * events (mode lifecycle, file uploads, exports/imports, per-message tool /
 * assistant / knowledge-base usage) are the only signal our server gets. They
 * are queued here and flushed in small batches to
 * `/api/confidential-mode/usage-log`, which persists them via the service-role
 * client into `public.confidential_usage_events`.
 *
 * Privacy: nothing identifying about the conversation content is sent. File
 * names are SHA-256 hashed before they reach this module. Failures are
 * swallowed — telemetry must never break the chat.
 */

export type ConfidentialUsageEntry =
  | { eventType: 'mode_activated'; providerMode: string }
  | { eventType: 'mode_closed' }
  | {
      eventType: 'file_uploaded';
      fileNameHash: string;
      fileType: string;
      fileSize: number;
    }
  | { eventType: 'data_exported'; chatCount: number; blobCount: number }
  | { eventType: 'data_imported'; chatCount: number; blobCount: number }
  | {
      eventType: 'message_sent';
      providerMode: string;
      modelId: string;
      assistantId: string | null;
      chatMode: string | null;
      webSearchUsed: boolean;
      knowledgeBaseIds: string[];
    };

const ENDPOINT = '/api/confidential-mode/usage-log';
const FLUSH_DELAY_MS = 4000;
const MAX_BATCH = 25;

let sessionId: string | null = null;
let queue: ConfidentialUsageEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pagehideBound = false;

function getSessionId(): string {
  if (!sessionId) {
    sessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 32)
        : Math.random().toString(36).slice(2).padEnd(32, '0').slice(0, 32);
  }
  return sessionId;
}

function bindPagehide(): void {
  if (pagehideBound || typeof window === 'undefined') return;
  pagehideBound = true;
  // Flush synchronously when the tab is hidden/closed so we don't lose the
  // tail of a session. `pagehide` is more reliable than `beforeunload`.
  window.addEventListener('pagehide', () => {
    void flushConfidentialUsage(true);
  });
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushConfidentialUsage();
  }, FLUSH_DELAY_MS);
}

/**
 * Queue a confidential usage event. Flushes immediately for lifecycle events
 * (so a quick open/close still records), otherwise batches with a short delay.
 */
export function recordConfidentialUsage(entry: ConfidentialUsageEntry): void {
  if (typeof window === 'undefined') return;

  // A new session starts on activation; the session id is cleared after close
  // so the next activation gets a fresh group.
  if (entry.eventType === 'mode_activated') {
    sessionId = null;
  }

  bindPagehide();
  queue.push(entry);

  const isLifecycle =
    entry.eventType === 'mode_activated' || entry.eventType === 'mode_closed';

  if (isLifecycle || queue.length >= MAX_BATCH) {
    void flushConfidentialUsage(false, entry.eventType === 'mode_closed');
  } else {
    scheduleFlush();
  }
}

/**
 * Flush the queued events. `useBeacon` uses `sendBeacon`/keepalive so the
 * request survives a page unload. `resetSession` clears the session id after
 * the flush (used when confidential mode closes).
 */
export async function flushConfidentialUsage(
  useBeacon = false,
  resetSession = false,
): Promise<void> {
  if (queue.length === 0) {
    if (resetSession) sessionId = null;
    return;
  }

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const entries = queue;
  queue = [];

  const body = JSON.stringify({
    sessionId: getSessionId(),
    entries,
    userAgent:
      typeof navigator !== 'undefined'
        ? navigator.userAgent.slice(0, 500)
        : undefined,
  });

  try {
    if (
      useBeacon &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      navigator.sendBeacon(
        ENDPOINT,
        new Blob([body], { type: 'application/json' }),
      );
    } else {
      await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: useBeacon,
      });
    }
  } catch {
    // Telemetry must never break the chat — drop on failure.
  } finally {
    if (resetSession) sessionId = null;
  }
}
